/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Handshake,
  Building2,
  Globe,
  Users,
  BadgeCheck,
  Headphones,
  ArrowRight,
  CheckCircle,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const B2BServicesPage = () => {
  const [formData, setFormData] = useState({
    company_name: "",
    contact_person: "",
    email: "",
    phone: "",
    city: "",
    service_required: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/b2b-enquiry`, formData);
      toast.success("Thank you! Our B2B team will contact you shortly.");
      setFormData({
        company_name: "",
        contact_person: "",
        email: "",
        phone: "",
        city: "",
        service_required: "",
        message: "",
      });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 },
  };

  const benefits = [
    { icon: BadgeCheck, title: "Competitive Rates", desc: "Best-in-market B2B hotel and package rates" },
    { icon: Globe, title: "Global Network", desc: "Access to worldwide destinations and suppliers" },
    { icon: Headphones, title: "Dedicated Support", desc: "Personal account manager for your business" },
    { icon: Users, title: "Group Handling", desc: "Expert coordination for groups of any size" },
  ];

  const services = [
    { title: "B2B Hotel Rates", desc: "Competitive contracted rates for hotels worldwide with instant confirmation" },
    { title: "DMC Services", desc: "Complete destination management including transfers, tours, and activities" },
    { title: "Ground Handling", desc: "Professional ground services including meet & greet, transfers, guides" },
    { title: "Group Coordination", desc: "End-to-end management of group tours and series departures" },
    { title: "MICE Solutions", desc: "Corporate events, conferences, incentive travel planning" },
    { title: "Visa Assistance", desc: "Support with visa documentation and processing" },
  ];

  return (
    <div data-testid="b2b-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-slate-900" data-testid="b2b-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/4940642/pexels-photo-4940642.jpeg?w=1920"
            alt="Business Partnership"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 to-slate-900" />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="caption text-gold mb-4 block">B2B Partnership</span>
            <h1 className="heading-1 text-white mb-6">Partner With Dnotours</h1>
            <p className="text-lg text-white/80">
              Join India's growing network of travel agents and access exclusive B2B rates, 
              reliable DMC services, and dedicated support for your business.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-padding bg-white" data-testid="b2b-benefits">
        <div className="container-custom">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="caption text-gold mb-4 block">Why Partner With Us</span>
            <h2 className="heading-2 text-primary mb-4">Benefits of Partnership</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
                data-testid={`benefit-${index}`}
              >
                <div className="w-16 h-16 mx-auto bg-gold/10 rounded-full flex items-center justify-center mb-6">
                  <benefit.icon className="h-8 w-8 text-gold" />
                </div>
                <h3 className="text-xl font-heading font-semibold text-primary mb-3">
                  {benefit.title}
                </h3>
                <p className="text-slate-600">{benefit.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="section-padding bg-accent" data-testid="b2b-services">
        <div className="container-custom">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="caption text-gold mb-4 block">Our B2B Services</span>
            <h2 className="heading-2 text-primary mb-4">What We Offer</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow" data-testid={`service-${index}`}>
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center mb-4">
                      <CheckCircle className="h-6 w-6 text-gold" />
                    </div>
                    <h3 className="text-xl font-heading font-semibold text-primary mb-3">
                      {service.title}
                    </h3>
                    <p className="text-slate-600">{service.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section className="section-padding bg-white" data-testid="b2b-form-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <motion.div {...fadeInUp}>
              <span className="caption text-gold mb-4 block">Join Our Network</span>
              <h2 className="heading-2 text-primary mb-6">Become a B2B Partner</h2>
              <p className="body-lg mb-8">
                Register as a B2B partner and get access to exclusive rates, priority support, 
                and a dedicated account manager to grow your business.
              </p>
              <div className="space-y-6">
                {[
                  "Quick quote turnaround within 4 hours",
                  "No registration or joining fees",
                  "Flexible credit terms available",
                  "24/7 emergency support line",
                  "Regular training and FAM trips",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-gold flex-shrink-0" />
                    <span className="text-slate-600">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Card className="shadow-xl" data-testid="b2b-form-card">
                <CardContent className="p-8">
                  <h3 className="text-xl font-heading font-semibold text-primary mb-6">
                    B2B Registration Form
                  </h3>
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="company_name">Company Name *</Label>
                        <Input
                          id="company_name"
                          name="company_name"
                          value={formData.company_name}
                          onChange={handleChange}
                          required
                          className="h-12"
                          data-testid="input-company-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contact_person">Contact Person *</Label>
                        <Input
                          id="contact_person"
                          name="contact_person"
                          value={formData.contact_person}
                          onChange={handleChange}
                          required
                          className="h-12"
                          data-testid="input-contact-person"
                        />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="h-12"
                          data-testid="input-email"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="h-12"
                          data-testid="input-phone"
                        />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="city">City *</Label>
                        <Input
                          id="city"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          required
                          className="h-12"
                          data-testid="input-city"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="service_required">Service Required *</Label>
                        <Select
                          value={formData.service_required}
                          onValueChange={(value) =>
                            setFormData({ ...formData, service_required: value })
                          }
                        >
                          <SelectTrigger className="h-12" data-testid="select-service">
                            <SelectValue placeholder="Select service" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="hotel_rates">B2B Hotel Rates</SelectItem>
                            <SelectItem value="dmc_services">DMC Services</SelectItem>
                            <SelectItem value="ground_handling">Ground Handling</SelectItem>
                            <SelectItem value="group_tours">Group Tours</SelectItem>
                            <SelectItem value="mice">MICE Solutions</SelectItem>
                            <SelectItem value="all">All Services</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message">Message (Optional)</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={4}
                        placeholder="Tell us about your business and requirements"
                        data-testid="textarea-message"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="btn-gold w-full"
                      disabled={isSubmitting}
                      data-testid="b2b-submit-btn"
                    >
                      {isSubmitting ? "Submitting..." : "Register as Partner"}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default B2BServicesPage;
